import { Component, ViewChild, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { LoadingController, ToastController } from "@ionic/angular";
import { debounceTime, distinctUntilChanged, pairwise, startWith } from "rxjs/operators";
import { BaseClass } from "src/app/shared/classes/base";
import { AutoSaveComponent } from "src/app/shared/components/autosave/autosave.component";
import { ProcessConfig, QuestionConfig, QuestionGroup, QuestionTypeEnum } from "src/app/shared/interfaces/process-config.interface";
import { ProcessStatusEnum, ProcessTypeEnum } from "src/app/shared/interfaces/process.interface";
import { TranslationService } from "src/app/shared/services/translation.service";
import { UtilService } from "src/app/shared/services/util.service";
import { QuestionAttributePipe } from "../../../shared/pipes/question-attribute.pipe";
import { QuestionGroupAttributesPipe } from "../../../shared/pipes/question-group-attributes.pipe";
import { Evaluation360Service } from "../../360-evaluation.service";
import { Evaluation360Record } from "../../_interfaces/360-evaluation-record.interface";
import { Evaluated360User, Evaluation360Status } from "../../_interfaces/360-evaluation-user.interface";
import { ConfigService } from "src/app/shared/services/multi-clients/config.service";

@Component({
    selector: "app-360-manage-evaluation",
    templateUrl: "./manage-evaluation.page.html",
    styleUrls: ["../../360-evaluation.page.scss", "./manage-evaluation.page.scss"],
})
export class ManageEvaluation360Page extends BaseClass implements OnInit {
    @ViewChild("autosave", { static: true }) autosave: AutoSaveComponent;

    public user: Evaluated360User;
    public processConfig: ProcessConfig;
    public allQuestions: Array<QuestionConfig> = [];
    public status: ProcessStatusEnum;
    public Evaluation360Status = Evaluation360Status;
    public editingEvaluation: Evaluation360Record;
    public questionsType;
    public formGroup: UntypedFormGroup;
    public allowAutoSave: boolean = true;
    public autoSaving: boolean = false;
    public hasPendingChanges: boolean = false;
    public processTypeConfig: ProcessTypeEnum = ProcessTypeEnum.ThreeSixtyEvaluation;

    private readonly _cycleId: number;
    private readonly _userId: string;

    constructor(
        protected _toastController: ToastController,
        protected _loadingController: LoadingController,
        protected _translationService: TranslationService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: Evaluation360Service,
        private _utilService: UtilService,
        private _questionGroupsAttributePipe: QuestionGroupAttributesPipe,
        private _questionAttributePipe: QuestionAttributePipe,
        private _configService: ConfigService,
    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(this._activatedRoute.snapshot.paramMap.get("cycleId"), 10);
        this._userId = this._activatedRoute.snapshot.paramMap.get("userId");
    }

    ngOnInit() {
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
    }

    ionViewWillEnter() {
        this._evaluationService.getApprovedUserFor360Evaluation(this._cycleId, this._userId).subscribe(
            async (response) => {
                this.processConfig = response.data.config;
                this.user = response.data.user;
                this.editingEvaluation = response.data.evaluation;

                if (this.processConfig.questions) {
                    this.allQuestions = [...this.allQuestions, ...this.processConfig.questions];
                } else {
                    let questions: QuestionConfig[] = [];
                    this.processConfig.questionsGroups.forEach((group) => {
                        questions = [...questions, ...group.questions];
                        questions = this._questionAttributePipe.transform(questions, response.data.user.attributes);
                    });
                    this.allQuestions = [...this.allQuestions, ...questions];
                }

                const formGroup = this._createFormGroup(this.processConfig.questionsGroups, this.editingEvaluation);

                if (this.processConfig.questionsGroups) {
                    this.processConfig.questionsGroups.forEach((group) => {
                        group.questions = this._utilService.groupQuestions(group.questions);
                        group.hasCalculateQuestions = this.hasCalculateType(group);
                    });
                }

                this.formGroup = formGroup;

                if (this.autosave) {
                    this.autosave.autoSaveAllowed$.subscribe((value: boolean) => {
                        this.allowAutoSave = value;
                    });
                }

                this.formGroup.valueChanges.pipe(startWith(this.formGroup.value), distinctUntilChanged(), pairwise()).subscribe(async ([previous, current]) => {
                    if (JSON.stringify(previous) === JSON.stringify(current)) return;
                    this.allowAutoSave = false;
                    const valuesToSet = await this._utilService.calculateRelatedQuestions(current, this.allQuestions, previous);
                    valuesToSet.forEach((result) => {
                        this.formGroup.get(result.field).setValue(result.value, { emitEvent: true, onlySelf: true });
                    });
                    this.allowAutoSave = true;
                });

                this.formGroup.valueChanges.pipe(distinctUntilChanged(), debounceTime(3000)).subscribe(async (evaluation) => {
                    if (this.allowAutoSave) {
                        this.hasPendingChanges = this.autoSaving && true;

                        if (!this.autoSaving) {
                            this.saveEvaluation(this.formGroup, this.user.isManager, true);
                        }
                    }
                });
            },
            (error) => this.notifyError(error),
        );
    }

    public goBack(): void {
        const stateUrl = this._utilService.setUrlStateGuard(ProcessTypeEnum.ThreeSixtyEvaluation);
        this._router.navigate(["360-evaluation/evaluation/" + this._cycleId], { state: stateUrl });
    }

    public saveEvaluation(formGroup: UntypedFormGroup, isManager: boolean, preventNotification: boolean = false): void {
        this.autoSaving = true;

        const evaluation: Evaluation360Record = formGroup.getRawValue();

        this._evaluationService.save360Evaluation(evaluation, isManager).subscribe(
            () => {
                if (!preventNotification) {
                    this.hasPendingChanges = false;
                    this.notifySuccess();
                }

                this.autoSaving = false;
            },
            (error) => {
                this.notifyError(error);
                this.autoSaving = false;
            },
        );
    }

    public sendEvaluation(formGroup: UntypedFormGroup, isManager: boolean): Promise<void> {
        if (!formGroup.valid) {
            formGroup["missingFields"] = true;

            this.notify(this._translationService.getTranslation("generic.requiredValueNotProvided"), "danger");
            return;
        }

        this.allowAutoSave = false;
        let evaluation: Evaluation360Record = formGroup.getRawValue();
        evaluation.language = this._translationService.getSelectedLanguage();
        this._evaluationService.send360Evaluation(evaluation, isManager).subscribe(
            () => {
                this.notifySuccess();
                this.goBack();
            },
            (error) => this.notifyError(error),
        );
    }

    private _createFormGroup(groups: Array<QuestionGroup>, editingEvaluation: Evaluation360Record = null): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            cycleId: new UntypedFormControl(this._cycleId),
            subjectUserId: new UntypedFormControl(this._userId),
        });

        const displayedGroups = this._questionGroupsAttributePipe.transform(groups, this.user.attributes);
        let currentIndex = 1;
        groups.forEach((group) => {
            group.questions.forEach((question) => {
                const controlName = "field" + currentIndex;
                let value = editingEvaluation ? editingEvaluation[controlName] : "";
                if (question.typeConfig.possibleValues && question.typeConfig.possibleValues.length > 0) {
                    if (!question.typeConfig.possibleValues.some((x) => x === value)) value = "";
                }

                const validators =
                    displayedGroups.some((g) => g.id === group.id) && (question.required || question.goal) && question.hideQuestion !== true ? [Validators.required] : [];
                formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                        {
                            value: value,
                            disabled: editingEvaluation && editingEvaluation.status === Evaluation360Status.Sent,
                        },
                        validators,
                    ),
                );

                question.currentIndex = currentIndex;
                currentIndex++;
            });
        });

        return formGroup;
    }

    private hasCalculateType(group: QuestionGroup) {
        return group.questions.some((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
    }
}
